/**
 * Transfert d'un brouillon prepare_reservation vers le tunnel Location
 * (panier localStorage) — sans créer de réservation.
 *
 * Le panier reste la seule porte d'entrée vers POST /gas/v1/rental/reservations.
 */

import {
    addItem,
    clearCart,
    setDraftMeta,
    type RentalCartItem,
} from '../rental-cart/store';

export interface TransferDraftInput {
    query_id: string;
    start_at: string;
    end_at: string;
    lines: Array<{
        item_id: number;
        quantity: number;
        name?: string;
        slug?: string;
        image_url?: string | null;
    }>;
    /** Si true, vide le panier avant d'appliquer le brouillon. */
    replace?: boolean;
    /**
     * dated : les lignes portent la période du brouillon (segment propre —
     * location en 2 temps) et s'AJOUTENT au panier existant sans toucher aux
     * dates globales. Défaut : les dates du brouillon deviennent la meta
     * globale du panier (comportement historique).
     */
    mode?: 'default' | 'dated';
}

export interface TransferDraftResult {
    ok: boolean;
    error?: string;
    item_count: number;
    query_id: string;
}

/**
 * Applique un brouillon matérialisé dans le panier + meta (dates + query_id).
 * Ne POST jamais de réservation.
 */
export function transferDraftToCart(input: TransferDraftInput): TransferDraftResult {
    const queryId = (input.query_id || '').trim();
    if (!queryId) {
        return { ok: false, error: 'missing_query_id', item_count: 0, query_id: '' };
    }
    if (!input.start_at || !input.end_at) {
        return { ok: false, error: 'missing_interval', item_count: 0, query_id: queryId };
    }
    const lines = (input.lines || []).filter((l) => l.item_id > 0 && l.quantity > 0);
    if (lines.length === 0) {
        return { ok: false, error: 'empty_lines', item_count: 0, query_id: queryId };
    }

    const dated = input.mode === 'dated';
    if (input.replace !== false && !dated) {
        clearCart();
    }

    for (const line of lines) {
        const item: Omit<RentalCartItem, 'qty' | 'free'> & { qty?: number; free?: boolean } = {
            item_id: line.item_id,
            name: line.name || `#${line.item_id}`,
            slug: line.slug || '',
            image_url: line.image_url ?? null,
            qty: line.quantity,
            start_at: dated ? input.start_at : null,
            end_at: dated ? input.end_at : null,
        };
        addItem(item);
    }

    if (!dated) {
        setDraftMeta({
            start_at: input.start_at,
            end_at: input.end_at,
            query_id: queryId,
            source: 'constrained_search',
        });
    }

    return {
        ok: true,
        item_count: lines.reduce((s, l) => s + l.quantity, 0),
        query_id: queryId,
    };
}
